import { BillingDetailsComponent } from '../components/customer-account/billing-details-component.js';
import { LegacyBillingDetailsComponent } from '../components/customer-account/legacy-billing-details-component.js';
import { Address } from '../models/serializable/address.js';
import { CustomerAccountBase } from '../models/serializable/customer-account-base.js';
import { CustomerProfile } from '../models/serializable/customer-profile.js';
import { InvoiceType } from '../models/serializable/invoicing/invoice-type.js';
import { PhoneNumber } from '../models/serializable/phone-number.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';

interface BillingSource {
  invoiceType: InvoiceType,
  firstName: string,
  lastName: string,
  formattedName: string,
  email: string,
  phoneNumber: PhoneNumber | null,
  component: BillingDetailsComponent | null,
  legacyComponent: LegacyBillingDetailsComponent | null,
}

export class BillingHelper {
  private constructor(private source: BillingSource | null) {}

  static empty(): BillingHelper {
    return new BillingHelper(null);
  }

  static fromCustomerAccount(account: CustomerAccountBase, invoiceType: InvoiceType): BillingHelper {
    const source = BillingHelper.getSourceFromCustomerAccount(account, invoiceType);
    return new BillingHelper(source);
  }

  static fromCustomerProfile(profile: CustomerProfile, invoiceType: InvoiceType): BillingHelper | null {
    const source = BillingHelper.getSourceFromCustomerPromise(profile, invoiceType);
    return source != null ? new BillingHelper(source) : null;
  }

  get hasSource(): boolean {
    return isNotNullOrUndefined(this.source);
  }

  updateFromCustomerAccount(account: CustomerAccountBase, invoiceType: InvoiceType): void {
    this.source = BillingHelper.getSourceFromCustomerAccount(account, invoiceType);
  }

  updateFromCustomerProfile(profile: CustomerProfile, invoiceType: InvoiceType): boolean {
    const source = BillingHelper.getSourceFromCustomerPromise(profile, invoiceType);
    if(source != null) {
      this.source = source;
    }

    return source != null;
  }

  getInvoiceType(): InvoiceType {
    return this.executeSafe((source) => source.invoiceType);
  }

  getFirstName(): string {
    return this.executeSafe((source) => source.firstName);
  }

  getLastName(): string {
    return this.executeSafe((source) => source.lastName);
  }

  getBillingName(): string {
    return this.executeSafe((source) => {
      const billingDetails = source.component?.getBillingProfile(source.invoiceType);
      let billingName = billingDetails?.name ?? null;

      if(isNullOrUndefined(billingName)) {
        billingName = source.legacyComponent?.name ?? null;
      }

      return billingName ?? source.formattedName;
    });
  }

  getBillingEmail(): string {
    return this.executeSafe((source) => {
      const billingDetails = source.component?.getBillingProfile(source.invoiceType);
      let billingEmail = billingDetails?.email ?? null;

      if(isNullOrUndefined(billingEmail)) {
        billingEmail = source.legacyComponent?.email ?? null;
      }

      return billingEmail ?? source.email;
    });
  }

  getBillingAddress(): Address | null {
    return this.executeSafe((source) => {
      const billingDetails = source.component?.getBillingProfile(source.invoiceType);
      let billingAddress = billingDetails?.address ?? null;

      if(isNullOrUndefined(billingAddress)) {
        billingAddress = source.legacyComponent?.address ?? null;
      }

      return billingAddress ?? null;
    });
  }

  getBillingTaxCode(): string | null {
    return this.executeSafe((source) => {
      if(source.invoiceType != InvoiceType.LegalEntity) {
        return null;
      }

      const billingDetails = source.component?.getBillingProfile(source.invoiceType);
      return billingDetails?.taxCode ?? null;
    });
  }

  getBillingGroupTaxCode(): string | null {
    return this.executeSafe((source) => {
      if(source.invoiceType != InvoiceType.LegalEntity) {
        return null;
      }

      const billingDetails = source.component?.getBillingProfile(source.invoiceType);
      return billingDetails?.groupTaxCode ?? null;
    });
  }

  getBillingPhoneNumber(): PhoneNumber | null {
    return this.executeSafe((source) => {
      const billingDetails = source.component?.getBillingProfile(source.invoiceType);
      let billingPhoneNumber = billingDetails?.phoneNumber ?? null;

      if(isNullOrUndefined(billingPhoneNumber)) {
        billingPhoneNumber = source.legacyComponent?.phoneNumber ?? null;
      }

      return billingPhoneNumber ?? source.phoneNumber;
    });
  }

  getFormattedBillingPhoneNumber(): string | null {
    const phoneNumber = this.getBillingPhoneNumber();
    return phoneNumber?.asFormattedString ?? null;
  }

  private executeSafe<T>(action: (source: BillingSource) => T): T {
    if(isNullOrUndefined(this.source)) {
      throw new Error('Billing helper does not have a source assigned.');
    }

    return action(this.source);
  }

  private static getSourceFromCustomerAccount(account: CustomerAccountBase, invoiceType: InvoiceType): BillingSource {
    return {
      invoiceType,
      firstName: account.firstName,
      lastName: account.lastName,
      formattedName: account.formattedName,
      email: account.email,
      phoneNumber: account.phoneNumber,
      component: account.findComponentByTypeId<BillingDetailsComponent>(BillingDetailsComponent.TYPE_ID),
      legacyComponent: account.findComponentByTypeId<LegacyBillingDetailsComponent>(LegacyBillingDetailsComponent.TYPE_ID),
    };
  }

  private static getSourceFromCustomerPromise(profile: CustomerProfile, invoiceType: InvoiceType): BillingSource | null {
    if(profile.firstName == null) return null;
    if(profile.lastName == null) return null;
    if(profile.formattedName == null) return null;
    if(profile.email == null) return null;

    return {
      invoiceType,
      firstName: profile.firstName,
      lastName: profile.lastName,
      formattedName: profile.formattedName,
      email: profile.email,
      phoneNumber: profile.phoneNumber,
      component: profile.findComponentByTypeId<BillingDetailsComponent>(BillingDetailsComponent.TYPE_ID),
      legacyComponent: profile.findComponentByTypeId<LegacyBillingDetailsComponent>(LegacyBillingDetailsComponent.TYPE_ID),
    };
  }
}
